import { useEffect } from 'react';

export interface DocumentHead {
  title: string;
  description: string;
  /** Absolute or rooted. */
  canonical: string;
  noIndex?: boolean;
  image?: string | null;
  /** The same page in other languages, this one included. Empty when there is no translation. */
  alternates?: { hreflang: string; href: string }[];
}

/**
 * Keeps the document's head in step with the page during client-side navigation.
 *
 * On a real page load the server has already written the right head — that is what search engines
 * and link previews read, and this hook changes nothing they see. It exists for the person who
 * clicks from one article to the next: without it the tab keeps the title of whichever page was
 * loaded first, and "copy link" on the third article shares the canonical of the first.
 *
 * Singletons (title, description, canonical, robots, the Open Graph basics) are updated in place
 * when present and created when not. What this hook created, it removes on the way out; what it
 * found and changed, it restores.
 */
export function useDocumentHead(head: DocumentHead | null) {
  useEffect(() => {
    if (!head) {
      return;
    }

    const undo: (() => void)[] = [];

    const previousTitle = document.title;
    document.title = head.title;
    undo.push(() => {
      document.title = previousTitle;
    });

    const absolute = (url: string) => (url.startsWith('http') ? url : `${window.location.origin}${url}`);

    function upsert(selector: string, create: () => HTMLElement, attribute: string, value: string) {
      let element = document.head.querySelector<HTMLElement>(selector);

      if (element) {
        const previous = element.getAttribute(attribute);
        element.setAttribute(attribute, value);
        undo.push(() => {
          if (previous === null) {
            element!.removeAttribute(attribute);
          } else {
            element!.setAttribute(attribute, previous);
          }
        });
      } else {
        element = create();
        element.setAttribute(attribute, value);
        document.head.appendChild(element);
        undo.push(() => element!.remove());
      }
    }

    const meta = (name: string, content: string) =>
      upsert(`meta[name="${name}"]`, () => Object.assign(document.createElement('meta'), { name }), 'content', content);

    const property = (name: string, content: string) =>
      upsert(
        `meta[property="${name}"]`,
        () => {
          const element = document.createElement('meta');
          element.setAttribute('property', name);
          return element;
        },
        'content',
        content,
      );

    meta('description', head.description);
    upsert('link[rel="canonical"]', () => Object.assign(document.createElement('link'), { rel: 'canonical' }), 'href', absolute(head.canonical));

    property('og:title', head.title);
    property('og:description', head.description);
    property('og:url', absolute(head.canonical));

    if (head.image) {
      property('og:image', absolute(head.image));
    }

    if (head.noIndex) {
      meta('robots', 'noindex, follow');
    } else {
      // Left over from a previous page that was noindex; this one is not.
      const robots = document.head.querySelector('meta[name="robots"]');
      if (robots) {
        const parent = robots.parentNode;
        const next = robots.nextSibling;
        robots.remove();
        undo.push(() => parent?.insertBefore(robots, next));
      }
    }

    // The pair belongs to the page, not to the site: whatever the previous page declared is wrong
    // here, so it is cleared and this page's own set written — which may well be none.
    const stale = Array.from(document.head.querySelectorAll('link[rel="alternate"][hreflang]'));
    stale.forEach((link) => link.remove());
    undo.push(() => stale.forEach((link) => document.head.appendChild(link)));

    for (const alternate of head.alternates ?? []) {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = alternate.hreflang;
      link.href = absolute(alternate.href);
      document.head.appendChild(link);
      undo.push(() => link.remove());
    }

    return () => {
      // In reverse, so an element created and then modified is restored before it is removed.
      undo.reverse().forEach((step) => step());
    };
  }, [head]);
}
